import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, MapPin, X, Plus, Edit, Trash2 } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const NotaryAppointmentModal = ({ isOpen, onClose, onUpdate }) => {
  const [appointments, setAppointments] = useState([]);
  const [notaries, setNotaries] = useState([]);
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '',
    property_id: '',
    notary_id: '',
    scheduled_date: '',
    appointment_type: 'Signature Acte',
    location: '',
    notes: '',
    duration_minutes: 60
  });

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch appointments with related data
      const { data: appointmentsData } = await supabase?.from('appointments')
        ?.select(`
          *,
          client:user_profiles!client_id(full_name, email),
          property:properties(title, address),
          notary:notaries(
            id,
            office_name,
            office_address,
            user:user_profiles(full_name, email, phone)
          )
        `)
        ?.eq('appointment_type', 'Notary Meeting')
        ?.order('scheduled_date', { ascending: false });

      // Fetch notaries
      const { data: notariesData } = await supabase?.from('notaries')
        ?.select(`
          *,
          user:user_profiles(full_name, email, phone)
        `);

      // Fetch properties
      const { data: propertiesData } = await supabase?.from('properties')
        ?.select('id, title, address')
        ?.eq('status', 'reserved')
        ?.order('title');

      // Fetch clients
      const { data: clientsData } = await supabase?.from('user_profiles')
        ?.select('id, full_name, email')
        ?.eq('role', 'client')
        ?.order('full_name');

      setAppointments(appointmentsData || []);
      setNotaries(notariesData || []);
      setProperties(propertiesData || []);
      setClients(clientsData || []);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    try {
      if (editingAppointment) {
        const { error } = await supabase?.from('appointments')
          ?.update({
            ...formData,
            appointment_type: 'Notary Meeting',
            updated_at: new Date()?.toISOString()
          })
          ?.eq('id', editingAppointment?.id);

        if (error) throw error;
      } else {
        const { error } = await supabase?.from('appointments')
          ?.insert({
            ...formData,
            appointment_type: 'Notary Meeting'
          });

        if (error) throw error;
      }

      setFormData({
        client_id: '',
        property_id: '',
        notary_id: '',
        scheduled_date: '',
        appointment_type: 'Signature Acte',
        location: '',
        notes: '',
        duration_minutes: 60
      });
      setEditingAppointment(null);
      setShowForm(false);
      fetchData();
      onUpdate?.();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const handleEdit = (appointment) => {
    setEditingAppointment(appointment);
    setFormData({
      client_id: appointment?.client_id || '',
      property_id: appointment?.property_id || '',
      notary_id: appointment?.notary_id || '',
      scheduled_date: appointment?.scheduled_date?.slice(0, 16) || '',
      appointment_type: appointment?.notes?.includes('Signature') ? 'Signature Acte' : 'Consultation',
      location: appointment?.location || '',
      notes: appointment?.notes || '',
      duration_minutes: appointment?.duration_minutes || 60
    });
    setShowForm(true);
  };

  const handleDelete = async (appointmentId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce rendez-vous ?')) {
      try {
        const { error } = await supabase?.from('appointments')
          ?.delete()
          ?.eq('id', appointmentId);

        if (error) throw error;
        fetchData();
        onUpdate?.();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose}></div>
        
        <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Gestion des RDV Notaires
              </h2>
              <p className="text-sm text-gray-500">
                Planifiez et gérez les rendez-vous avec les notaires
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {!showForm ? (
              <>
                {/* Actions */}
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-gray-900">
                    Rendez-vous Programmés ({appointments?.length})
                  </h3>
                  <Button
                    onClick={() => setShowForm(true)}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Nouveau RDV
                  </Button>
                </div>

                {/* Appointments List */}
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : appointments?.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Aucun rendez-vous programmé</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {appointments?.map((appointment) => (
                      <div
                        key={appointment?.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-4 mb-3">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Calendar className="h-4 w-4" />
                                {new Date(appointment?.scheduled_date)?.toLocaleDateString('fr-FR', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Clock className="h-4 w-4" />
                                {new Date(appointment?.scheduled_date)?.toLocaleTimeString('fr-FR', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                appointment?.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                appointment?.status === 'scheduled'? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {appointment?.status === 'confirmed' ? 'Confirmé' :
                                 appointment?.status === 'scheduled' ? 'Programmé' : 'En attente'}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <User className="h-4 w-4 text-gray-400" />
                                  <span className="text-sm font-medium">Client:</span>
                                  <span className="text-sm text-gray-600">
                                    {appointment?.client?.full_name}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mb-2">
                                  <MapPin className="h-4 w-4 text-gray-400" />
                                  <span className="text-sm font-medium">Propriété:</span>
                                  <span className="text-sm text-gray-600">
                                    {appointment?.property?.title}
                                  </span>
                                </div>
                              </div>
                              
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <User className="h-4 w-4 text-gray-400" />
                                  <span className="text-sm font-medium">Notaire:</span>
                                  <span className="text-sm text-gray-600">
                                    {appointment?.notary?.user?.full_name} - {appointment?.notary?.office_name}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mb-2">
                                  <MapPin className="h-4 w-4 text-gray-400" />
                                  <span className="text-sm font-medium">Lieu:</span>
                                  <span className="text-sm text-gray-600">
                                    {appointment?.location}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {appointment?.notes && (
                              <p className="text-sm text-gray-600 mt-2">
                                <strong>Notes:</strong> {appointment?.notes}
                              </p>
                            )}
                          </div>

                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(appointment)}
                              className="flex items-center gap-1"
                            >
                              <Edit className="h-3 w-3" />
                              Modifier
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(appointment?.id)}
                              className="flex items-center gap-1 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-3 w-3" />
                              Supprimer
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              /* Form */
              (<form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingAppointment ? 'Modifier le RDV' : 'Nouveau RDV Notaire'}
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setEditingAppointment(null);
                    }}
                  >
                    Annuler
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client
                    </label>
                    <Select
                      value={formData?.client_id}
                      onChange={(e) => setFormData({...formData, client_id: e?.target?.value})}
                      required
                    >
                      <option value="">Sélectionner un client</option>
                      {clients?.map((client) => (
                        <option key={client?.id} value={client?.id}>
                          {client?.full_name} ({client?.email})
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Propriété
                    </label>
                    <Select
                      value={formData?.property_id}
                      onChange={(e) => setFormData({...formData, property_id: e?.target?.value})}
                      required
                    >
                      <option value="">Sélectionner une propriété</option>
                      {properties?.map((property) => (
                        <option key={property?.id} value={property?.id}>
                          {property?.title} - {property?.address}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notaire
                    </label>
                    <Select
                      value={formData?.notary_id}
                      onChange={(e) => setFormData({...formData, notary_id: e?.target?.value})}
                      required
                    >
                      <option value="">Sélectionner un notaire</option>
                      {notaries?.map((notary) => (
                        <option key={notary?.id} value={notary?.id}>
                          {notary?.user?.full_name} - {notary?.office_name}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date et Heure
                    </label>
                    <Input
                      type="datetime-local"
                      value={formData?.scheduled_date}
                      onChange={(e) => setFormData({...formData, scheduled_date: e?.target?.value})}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type de RDV
                    </label>
                    <Select
                      value={formData?.appointment_type}
                      onChange={(e) => setFormData({...formData, appointment_type: e?.target?.value})}
                    >
                      <option value="Signature Acte">Signature Acte</option>
                      <option value="Consultation">Consultation</option>
                      <option value="Compromis de Vente">Compromis de Vente</option>
                      <option value="État des Lieux">État des Lieux</option>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Durée (minutes)
                    </label>
                    <Input
                      type="number"
                      value={formData?.duration_minutes}
                      onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e?.target?.value)})}
                      min="30"
                      step="15"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lieu du RDV
                  </label>
                  <Input
                    value={formData?.location}
                    onChange={(e) => setFormData({...formData, location: e?.target?.value})}
                    placeholder="Adresse du rendez-vous"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData?.notes}
                    onChange={(e) => setFormData({...formData, notes: e?.target?.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Notes additionnelles..."
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setEditingAppointment(null);
                    }}
                  >
                    Annuler
                  </Button>
                  <Button type="submit">
                    {editingAppointment ? 'Mettre à jour' : 'Programmer'}
                  </Button>
                </div>
              </form>)
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotaryAppointmentModal;